import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, CheckCircle2, Loader2, ShieldAlert, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { PageGuard } from '@components/PageGuard';
import {
  withdrawalService,
  withdrawalStatusChip,
  type WithdrawalRequest,
} from '@services/withdrawal.service';
import { usePermission } from '@hooks/usePermission';
import { cn, formatCurrency } from '@lib/utils';
import { formatTimestamp, humanizeCode, shortId } from './ledgerFormat';

const FILTERS = [
  { value: '', label: 'Awaiting action' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed', label: 'Failed' },
];

function apiMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown };
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
  }
  return fallback;
}

/**
 * WITHDRAWALS QUEUE — the money actually leaving Chekit.
 *
 * Approving costs nothing: funds are already held, so approval only says "yes,
 * pay this". Completing is the irreversible step — it debits the wallet and
 * books the liability — so it demands a payout reference and is styled as the
 * dangerous action it is.
 */
export const WithdrawalsPage = () => {
  const queryClient = useQueryClient();
  const { hasCheKitPermission } = usePermission();
  const canProcess = hasCheKitPermission('payments.process');

  const [status, setStatus] = useState('');
  const [active, setActive] = useState<WithdrawalRequest | null>(null);
  const [payoutReference, setPayoutReference] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['treasury', 'withdrawals', status],
    queryFn: () => withdrawalService.queue({ status: status || undefined, limit: 200 }),
  });

  const rows = useMemo(() => data ?? [], [data]);
  const openTotal = rows.reduce((sum, r) => sum + Number(r.amount ?? 0), 0);

  const done = (message: string) => {
    toast.success(message);
    setActive(null);
    setPayoutReference('');
    setReason('');
    setError(null);
    queryClient.invalidateQueries({ queryKey: ['treasury'] });
    queryClient.invalidateQueries({ queryKey: ['ledger'] });
  };

  const approveM = useMutation({
    mutationFn: (id: string) => withdrawalService.approve(id),
    onSuccess: () => done('Approved — funds stay held until you record the payout.'),
    onError: (e) => setError(apiMessage(e, 'Could not approve.')),
  });

  const completeM = useMutation({
    mutationFn: ({ id, ref }: { id: string; ref: string }) =>
      withdrawalService.complete(id, ref),
    onSuccess: () => done('Payout recorded — wallet debited, liability booked.'),
    onError: (e) => setError(apiMessage(e, 'Could not record the payout.')),
  });

  const rejectM = useMutation({
    mutationFn: ({ id, why }: { id: string; why: string }) =>
      withdrawalService.reject(id, why),
    onSuccess: () => done('Rejected — the hold has been released.'),
    onError: (e) => setError(apiMessage(e, 'Could not reject.')),
  });

  const busy = approveM.isPending || completeM.isPending || rejectM.isPending;

  return (
    <PageGuard permission="wallets.read">
      <div className="space-y-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
              Withdrawals
            </h1>
            <p className="mt-1 text-[14px] text-[var(--text-muted)]">
              Money leaving Chekit. Approving only says yes — the funds are already
              held. Completing debits the wallet and books what we owe until the
              cash actually clears.
            </p>
          </div>
          {!status && rows.length > 0 && (
            <div className="rounded-3xl bg-[var(--bg-surface)] px-6 py-4 shadow-soft">
              <div className="text-[13px] text-[var(--text-muted)]">Awaiting action</div>
              <div className="font-display mt-1 text-[28px] font-medium leading-none tracking-[-0.03em] text-[var(--text-primary)]">
                {formatCurrency(openTotal)}
              </div>
              <div className="mt-1 text-[12px] text-[var(--text-muted)]">
                across {rows.length} request{rows.length === 1 ? '' : 's'}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatus(f.value)}
              className={cn(
                'rounded-full px-4 py-2 text-[13px] font-semibold transition-colors',
                status === f.value
                  ? 'bg-[var(--text-primary)] text-[var(--bg-surface)]'
                  : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:bg-[var(--bg-base)]',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-2xl bg-[var(--state-danger-soft,rgba(220,38,38,0.08))] px-4 py-3">
            <ShieldAlert
              className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-danger)]"
              strokeWidth={1.5}
            />
            <p className="text-[13px] text-[var(--state-danger)]">{error}</p>
          </div>
        )}

        <div className="overflow-hidden rounded-3xl bg-[var(--bg-surface)] shadow-soft">
          {isLoading && (
            <div className="flex items-center gap-2 p-12 text-[14px] text-[var(--text-muted)]">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              Loading withdrawals…
            </div>
          )}

          {!isLoading && rows.length === 0 && (
            <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
              {status
                ? 'Nothing with that status.'
                : 'Nothing awaiting action — every withdrawal has been dealt with.'}
            </div>
          )}

          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[14px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    <th className="px-6 py-4 font-semibold">Reference</th>
                    <th className="px-6 py-4 font-semibold">Owner</th>
                    <th className="px-6 py-4 text-right font-semibold">Amount</th>
                    <th className="px-6 py-4 font-semibold">Method</th>
                    <th className="px-6 py-4 font-semibold">Status</th>
                    <th className="px-6 py-4 font-semibold">Requested</th>
                    <th className="px-6 py-4" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-[var(--border-subtle)]">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-[var(--text-primary)]">
                          {r.reference}
                        </div>
                        <Link
                          to={`/finance/wallets/${r.wallet_id}`}
                          className="text-[12px] text-[var(--text-muted)] hover:underline"
                        >
                          wallet {shortId(r.wallet_id)}
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        {r.owner_id ? (
                          <Link
                            to={`/finance/owners/${r.owner_type}/${r.owner_id}`}
                            className="text-[var(--text-secondary)] hover:underline"
                          >
                            {humanizeCode(r.owner_type)} · {shortId(r.owner_id)}
                          </Link>
                        ) : (
                          <span className="text-[var(--text-muted)]">system</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="font-semibold text-[var(--text-primary)]">
                          {formatCurrency(Number(r.amount))}
                        </div>
                        {Number(r.fee_amount) > 0 && (
                          <div className="text-[12px] text-[var(--text-muted)]">
                            net {formatCurrency(Number(r.net_amount))}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-[var(--text-secondary)]">
                        {humanizeCode(r.payout_method)}
                        {r.payout_reference && (
                          <div className="text-[12px] text-[var(--text-muted)]">
                            {r.payout_reference}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={cn(
                            'rounded-full px-2.5 py-1 text-[12px] font-semibold',
                            withdrawalStatusChip(r.status),
                          )}
                        >
                          {humanizeCode(r.status)}
                        </span>
                        {r.completed_by && (
                          // Who released the money — the first thing asked when a
                          // payout is queried, so it belongs in the list, not
                          // buried in a detail view.
                          <div className="mt-1 text-[11px] text-[var(--text-muted)]">
                            by {shortId(r.completed_by)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-[13px] text-[var(--text-muted)]">
                        {formatTimestamp(r.requested_at)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {canProcess &&
                          ['pending', 'approved', 'processing'].includes(r.status) && (
                            <button
                              type="button"
                              onClick={() => {
                                setActive(r);
                                setPayoutReference('');
                                setReason('');
                                setError(null);
                              }}
                              className="rounded-xl bg-[var(--bg-base)] px-3 py-1.5 text-[13px] font-semibold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-elevated,var(--bg-base))]"
                            >
                              Review
                            </button>
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Review ─────────────────────────────────────────────────────── */}
      {active && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/55 p-4">
          <div className="my-8 w-full max-w-lg rounded-3xl bg-[var(--bg-surface)] shadow-lift">
            <div className="border-b border-[var(--border-subtle)] px-6 py-5">
              <h2 className="font-display text-[20px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
                {active.reference}
              </h2>
              <p className="mt-1 text-[13px] text-[var(--text-muted)]">
                {formatCurrency(Number(active.amount))} to{' '}
                {humanizeCode(active.payout_method)} ·{' '}
                {humanizeCode(active.owner_type)}
              </p>
            </div>

            <div className="space-y-5 px-6 py-5">
              {active.payout_details && Object.keys(active.payout_details).length > 0 && (
                <div className="rounded-2xl bg-[var(--bg-base)] p-4">
                  <p className="mb-2 text-[12px] font-semibold text-[var(--text-secondary)]">
                    Pay to
                  </p>
                  {Object.entries(active.payout_details).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3 text-[13px]">
                      <span className="text-[var(--text-muted)]">{humanizeCode(k)}</span>
                      <span className="font-medium text-[var(--text-primary)]">
                        {String(v)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 rounded-2xl bg-[var(--state-danger-soft,rgba(220,38,38,0.08))] px-4 py-3">
                  <ShieldAlert
                    className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-danger)]"
                    strokeWidth={1.5}
                  />
                  <p className="text-[13px] text-[var(--state-danger)]">{error}</p>
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                  Payout reference
                </label>
                <input
                  value={payoutReference}
                  onChange={(e) => setPayoutReference(e.target.value)}
                  placeholder="M-Pesa receipt, cheque number, bank reference"
                  className="w-full rounded-xl bg-[var(--bg-base)] px-3 py-2 text-[14px] text-[var(--text-primary)] outline-none"
                />
                <p className="mt-1.5 text-[12px] text-[var(--text-muted)]">
                  Required to complete. This is the proof the money went out —
                  without it a completed payout cannot be told apart from a mistake.
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                  Reason <span className="text-[var(--text-muted)]">(rejection only)</span>
                </label>
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why this is being refused — the requester sees it"
                  className="w-full rounded-xl bg-[var(--bg-base)] px-3 py-2 text-[14px] text-[var(--text-primary)] outline-none"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-subtle)] px-6 py-4">
              <button
                type="button"
                onClick={() => setActive(null)}
                className="rounded-xl px-4 py-2 text-[13px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--bg-base)]"
              >
                Close
              </button>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy || !reason.trim()}
                  onClick={() => rejectM.mutate({ id: active.id, why: reason.trim() })}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--bg-base)] px-4 py-2 text-[13px] font-semibold text-[var(--state-danger)] disabled:opacity-40"
                >
                  <XCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Reject
                </button>

                {active.status === 'pending' && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => approveM.mutate(active.id)}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--bg-base)] px-4 py-2 text-[13px] font-semibold text-[var(--text-primary)] disabled:opacity-40"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Approve
                  </button>
                )}

                <button
                  type="button"
                  disabled={busy || !payoutReference.trim()}
                  onClick={() =>
                    completeM.mutate({ id: active.id, ref: payoutReference.trim() })
                  }
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--state-danger)] px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-40"
                >
                  {busy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
                  ) : (
                    <Banknote className="h-3.5 w-3.5" strokeWidth={1.5} />
                  )}
                  Record payout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageGuard>
  );
};
